import { useEffect, useReducer } from 'react';
import { useRouter } from 'next/router';

import {
  afterHoursEnabledForDelivery,
  afterHoursEnabledForDeliveryV2,
  afterHoursEnabledForPickup,
  afterHoursEnabledForPickupV2,
  scheduledOrderingEnabledForDelivery,
  scheduledOrderingEnabledForDeliveryV2,
  getPickupMinimum,
  getDeliveryMinimum,
  getDeliveryMinimumV2,
  getPickupMinimumV2,
} from 'shared/helpers/dispensaries';
import { openInfoForDispensaryV2 } from 'shared/core/helpers/dispensaries';
import useErnie from 'shared/hooks/use-ernie';
import useCart from 'hooks/use-cart';
import useUI from 'hooks/use-ui';
import useGoToCheckout from 'hooks/use-go-to-checkout';
import useCustomerState from 'hooks/use-customer-state';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import { useCartViewedMonitor } from 'src/utils/analytics/monitors/use-cart-viewed-monitor';

import { cartItemId } from 'utils/helpers/product';
import useAppliedOffers from 'hooks/use-applied-offers';

function dropdownReducer(state, action) {
  if (state.openDropdown === action.openDropdown) {
    return { openDropdown: null };
  }
  return { openDropdown: action.openDropdown };
}

function useOrderTypeStateHandlers() {
  // TODO: concider simplifing by using useState
  const [{ openDropdown }, dispatch] = useReducer(dropdownReducer, {});

  const handleOpenOrderTypeDropdown = () => {
    dispatch({ openDropdown: 'orderType' });
  };

  const handleCloseOrderTypeDropdown = () => {
    dispatch({ openDropdown: null });
  };

  const isOrderTypeDropdownOpen = openDropdown === 'orderType';

  return {
    handleOpenOrderTypeDropdown,
    handleCloseOrderTypeDropdown,
    isOrderTypeDropdownOpen,
  };
}

export function useCartDropdown({ onClose }) {
  const {
    dispensary,
    durationEstimates,
    isDelivery,
    isPickup,
    itemCount,
    items,
    menuType,
    openInfo,
    simplifiedOrderType,
    subtotal,
    taxInclusionNote,
    changeItemWeight,
    changeItemQuantity,
    removeItem,
    isEmpty,
  } = useCart();

  const { isKiosk, showShop, isEmbedded, previewMode } = useUI();

  const showErnie = useErnie();
  const goToCheckout = useGoToCheckout(`in-cart`);

  const {
    handleOpenOrderTypeDropdown,
    handleCloseOrderTypeDropdown,
    isOrderTypeDropdownOpen,
  } = useOrderTypeStateHandlers();

  const customerState = useCustomerState();
  const router = useRouter();

  useCartViewedMonitor();

  const handleGoToCheckout = () => {
    if (goToCheckout()) {
      onClose();
    }
  };

  const isOrderTypesConfigV2 = useDispensaryFlag(`rollout.use-updated-ordering-configurations`, dispensary?.id);
  const openInfoV2 = openInfoForDispensaryV2(dispensary, { previewMode });

  const offerAnyPickupService = isOrderTypesConfigV2
    ? dispensary?.orderTypesConfigV2?.offerAnyPickupService
    : dispensary?.offerAnyPickupService;
  const offerDelivery = isOrderTypesConfigV2
    ? dispensary?.orderTypesConfigV2?.offerDeliveryService
    : dispensary?.offerDelivery;

  const afterHoursOrderingForPickup = isOrderTypesConfigV2
    ? afterHoursEnabledForPickupV2(dispensary)
    : afterHoursEnabledForPickup(dispensary);

  const afterHoursOrderingForDelivery = isOrderTypesConfigV2
    ? afterHoursEnabledForDeliveryV2(dispensary)
    : afterHoursEnabledForDelivery(dispensary);

  const scheduledOrderingForDelivery = isOrderTypesConfigV2
    ? scheduledOrderingEnabledForDeliveryV2(dispensary)
    : scheduledOrderingEnabledForDelivery(dispensary);

  const { discountToCartApplied, appliedOffers } = useAppliedOffers();

  const { delivery, pickup } = openInfo;
  const { inStorePickup, curbsidePickup, driveThruPickup, delivery: deliveryV2 } = openInfoV2;

  const pickupIsOpen = isOrderTypesConfigV2
    ? inStorePickup.isOpen || curbsidePickup.isOpen || driveThruPickup.isOpen
    : pickup.isOpen;

  const deliveryIsOpen = isOrderTypesConfigV2 ? deliveryV2.isOpen : delivery.isOpen;

  let displayOrderTypeSwitch;

  if (isKiosk) {
    displayOrderTypeSwitch = false;
  } else if (dispensary) {
    if (isDelivery) {
      displayOrderTypeSwitch = offerAnyPickupService && (pickupIsOpen || afterHoursOrderingForPickup);
    } else if (isPickup) {
      displayOrderTypeSwitch =
        dispensary.deliveryInfo?.withinBounds &&
        offerDelivery &&
        (deliveryIsOpen || (afterHoursOrderingForDelivery && scheduledOrderingForDelivery));
    }
  }

  const showEstimate =
    dispensary && offerDelivery && !dispensary.featureFlags?.hideDeliveryEstimate && !scheduledOrderingForDelivery;

  const handleWeightChange = (item) => (e) => {
    const { value } = e.target;
    changeItemWeight(item, value, showErnie, customerState);
  };

  const handleQuantityChange = (item) => (event) => {
    const id = cartItemId(item);
    const value = Number(event.target.value);
    changeItemQuantity(id, value, showErnie, customerState);
  };

  const handleRemoveItem = (item) => () => {
    removeItem(item.key);
  };

  let hasNotMetMinimum = false;
  let minimum = 0;

  if (!isEmpty) {
    if (isOrderTypesConfigV2) {
      minimum = isDelivery ? getDeliveryMinimumV2(dispensary, true) : getPickupMinimumV2(dispensary, `inStorePickup`);
    } else {
      minimum = isDelivery ? getDeliveryMinimum(dispensary, true, isOrderTypesConfigV2) : getPickupMinimum(dispensary);
    }
    hasNotMetMinimum = minimum > subtotal;
  }

  useEffect(() => {
    /* eslint-disable i18next/no-literal-string */
    router.events.on('routeChangeComplete', onClose);

    return () => {
      router.events.off('routeChangeComplete', onClose);
    };
    /* eslint-enable i18next/no-literal-string */
  }, [router, onClose]);

  return {
    appliedOffers,
    discountToCartApplied,
    dispensary,
    displayOrderTypeSwitch,
    durationEstimates,
    handleCloseOrderTypeDropdown,
    handleGoToCheckout,
    handleOpenOrderTypeDropdown,
    handleQuantityChange,
    handleRemoveItem,
    handleWeightChange,
    hasNotMetMinimum,
    isEmbedded,
    isKiosk,
    isOrderTypeDropdownOpen,
    itemCount,
    items,
    menuType,
    minimum,
    showEstimate,
    showShop,
    simplifiedOrderType,
    subtotal,
    taxInclusionNote,
  };
}
